import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Box, Typography, Button, Stack, IconButton } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs, { Dayjs } from 'dayjs';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useLanguageContext } from '../../../data-access';
import { getDayjsLocaleForLanguage } from '../../../i18n/dayjs';
import './WeekDatePickerModal.scss';

const MAX_RANGE_DAYS = 7;

interface WeekDatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Dayjs;
  endDate?: Dayjs;
  onSelectWeek: (startDate: Dayjs, endDate: Dayjs) => void;
}

const WeekDatePickerModal: React.FC<WeekDatePickerModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
  endDate,
  onSelectWeek,
}) => {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const dateLocale = getDayjsLocaleForLanguage(language);
  const today = useMemo(() => dayjs().startOf('day'), []);
  const getUpcomingFriday = (date: Dayjs) => {
    const daysUntilFriday = (5 - date.day() + 7) % 7;
    return date.add(daysUntilFriday, 'day').startOf('day');
  };

  const [draftStartDate, setDraftStartDate] = useState(selectedDate.startOf('day'));
  const [draftEndDate, setDraftEndDate] = useState(
    (endDate ?? getUpcomingFriday(today)).startOf('day')
  );
  const [selectionStep, setSelectionStep] = useState<'start' | 'end'>('start');

  useEffect(() => {
    if (!isOpen) return;

    const defaultStart = today;
    const defaultEnd = getUpcomingFriday(today);
    const nextStart = selectedDate.startOf('day').isBefore(today, 'day')
      ? defaultStart
      : selectedDate.startOf('day');
    const candidateEnd = (endDate ?? defaultEnd).startOf('day');
    const nextEnd = candidateEnd.isBefore(nextStart, 'day') ? nextStart : candidateEnd;
    const maxEndDate = nextStart.add(MAX_RANGE_DAYS - 1, 'day');
    const clampedEnd = nextEnd.isAfter(maxEndDate, 'day') ? maxEndDate : nextEnd;

    setDraftStartDate(nextStart);
    setDraftEndDate(clampedEnd);
    setSelectionStep('start');
  }, [isOpen, selectedDate, endDate, today]);

  return (
    <Modal open={isOpen} onClose={onClose} className="week-date-picker-modal">
      <Box className="week-date-picker-modal__content">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className="week-date-picker-modal__header"
        >
          <Typography variant="h6" className="week-date-picker-modal__title">
            {t('task.scheduler.datePicker.selectDateRange')}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('task.scheduler.datePicker.closeModal')}
            onClick={onClose}
            className="week-date-picker-modal__close-button"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <DateCalendar
          className="week-date-picker-modal__calendar"
          value={selectionStep === 'start' ? draftStartDate : draftEndDate}
          views={['day']}
          onChange={(value) => {
            if (!value) return;
            const date = value.startOf('day');

            if (selectionStep === 'start') {
              setDraftStartDate(date);
              const maxEndDate = date.add(MAX_RANGE_DAYS - 1, 'day');
              const nextEnd = draftEndDate.isBefore(date, 'day')
                ? date
                : draftEndDate.isAfter(maxEndDate, 'day')
                  ? maxEndDate
                  : draftEndDate;
              setDraftEndDate(nextEnd);
              setSelectionStep('end');
              return;
            }

            const maxEndDate = draftStartDate.add(MAX_RANGE_DAYS - 1, 'day');
            const normalizedEnd = date.isBefore(draftStartDate, 'day')
              ? draftStartDate
              : date.isAfter(maxEndDate, 'day')
                ? maxEndDate
                : date;
            setDraftEndDate(normalizedEnd);
          }}
          shouldDisableDate={(date) => {
            const normalizedDate = date.startOf('day');
            if (normalizedDate.isBefore(today, 'day')) {
              return true;
            }
            if (selectionStep === 'end') {
              return normalizedDate.isAfter(draftStartDate.add(MAX_RANGE_DAYS - 1, 'day'), 'day');
            }
            return false;
          }}
          slots={{
            day: (dayProps: PickersDayProps<Dayjs>) => {
              const { day, outsideCurrentMonth, ...rest } = dayProps;
              const inSelectedRange =
                !outsideCurrentMonth &&
                day.isSameOrAfter(draftStartDate, 'day') &&
                day.isSameOrBefore(draftEndDate, 'day');
              const isRangeStart = inSelectedRange && day.isSame(draftStartDate, 'day');
              const isRangeEnd = inSelectedRange && day.isSame(draftEndDate, 'day');

              return (
                <PickersDay
                  {...rest}
                  day={day}
                  outsideCurrentMonth={outsideCurrentMonth}
                  sx={{
                    ...(inSelectedRange
                      ? {
                          backgroundColor: 'var(--color-primary-light)',
                          borderRadius: 0,
                          color: 'var(--color-text-primary, inherit)',
                        }
                      : {}),
                    ...(isRangeStart
                      ? {
                          borderTopLeftRadius: '999px',
                          borderBottomLeftRadius: '999px',
                        }
                      : {}),
                    ...(isRangeEnd
                      ? {
                          borderTopRightRadius: '999px',
                          borderBottomRightRadius: '999px',
                        }
                      : {}),
                    '&.Mui-selected': {
                      backgroundColor: 'var(--color-primary) !important',
                      color: 'var(--color-white-lightest) !important',
                      borderRadius: '999px',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'var(--color-primary) !important',
                    },
                  }}
                />
              );
            },
          }}
        />

        <Stack direction="row" spacing={1} className="week-date-picker-modal__range-row">
          <Button
            size="small"
            variant={selectionStep === 'start' ? 'contained' : 'outlined'}
            onClick={() => setSelectionStep('start')}
            className={clsx(
              'week-date-picker-modal__range-chip',
              selectionStep === 'start'
                ? 'week-date-picker-modal__range-chip--active'
                : 'week-date-picker-modal__range-chip--inactive'
            )}
          >
            {t('task.scheduler.datePicker.startDate', {
              date: draftStartDate.locale(dateLocale).format('MMM DD'),
            })}
          </Button>
          <Typography variant="body2" className="week-date-picker-modal__range-separator">
            –
          </Typography>
          <Button
            size="small"
            variant={selectionStep === 'end' ? 'contained' : 'outlined'}
            onClick={() => setSelectionStep('end')}
            className={clsx(
              'week-date-picker-modal__range-chip',
              selectionStep === 'end'
                ? 'week-date-picker-modal__range-chip--active'
                : 'week-date-picker-modal__range-chip--inactive'
            )}
          >
            {t('task.scheduler.datePicker.endDate', {
              date: draftEndDate.locale(dateLocale).format('MMM DD'),
            })}
          </Button>
        </Stack>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          className="week-date-picker-modal__actions"
        >
          <Button
            variant="contained"
            className="week-date-picker-modal__select-button"
            onClick={() => {
              onSelectWeek(draftStartDate, draftEndDate);
              onClose();
            }}
          >
            {t('task.scheduler.datePicker.select')}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default WeekDatePickerModal;
